import { motion } from 'framer-motion';
import { TrendingUp, ChevronUp, ChevronDown, Package, BarChart3, Users, Clock, Star, DollarSign, Activity } from 'lucide-react';
import { PieChart, Pie, Cell, ResponsiveContainer, Tooltip, AreaChart, Area, CartesianGrid, XAxis, YAxis, LineChart, Line, BarChart, Bar } from 'recharts';

const DataAnalytics = () => {
  const keyMetrics = [
    {
      icon: "📦",
      value: "2.5M+",
      title: "Orders Delivered",
      description: "Monthly volume",
      trend: "up",
      change: "+23%"
    },
    {
      icon: "🚀",
      value: "8.2 min",
      title: "Avg Delivery Time",
      description: "Record speed",
      trend: "down",
      change: "-15%"
    },
    {
      icon: "🏪",
      value: "15K+",
      title: "Partner Stores",
      description: "Growing network",
      trend: "up",
      change: "+34%"
    },
    {
      icon: "🌟",
      value: "4.8/5",
      title: "Customer Rating",
      description: "User satisfaction",
      trend: "up",
      change: "+0.3"
    },
    {
      icon: "💰",
      value: "₹450Cr",
      title: "GMV",
      description: "Monthly volume",
      trend: "up",
      change: "+67%"
    },
    {
      icon: "📱",
      value: "5M+",
      title: "App Downloads",
      description: "Total installs",
      trend: "up",
      change: "+89%"
    }
  ];

  const marketData = [
    { category: 'Groceries', percentage: 35, color: '#ef4444', growth: '+12%', value: 875000 },
    { category: 'Food & Beverages', percentage: 25, color: '#f59e0b', growth: '+18%', value: 625000 },
    { category: 'Health & Beauty', percentage: 20, color: '#10b981', growth: '+25%', value: 500000 },
    { category: 'Electronics', percentage: 12, color: '#3b82f6', growth: '+8%', value: 300000 },
    { category: 'Fashion', percentage: 5, color: '#8b5cf6', growth: '+15%', value: 125000 },
    { category: 'Others', percentage: 3, color: '#6b7280', growth: '+5%', value: 75000 }
  ];

  const growthData = [
    { month: 'Jan', orders: 45000, revenue: 280, users: 125000, satisfaction: 4.2 },
    { month: 'Feb', orders: 52000, revenue: 320, users: 142000, satisfaction: 4.3 },
    { month: 'Mar', orders: 61000, revenue: 380, users: 168000, satisfaction: 4.5 },
    { month: 'Apr', orders: 73000, revenue: 450, users: 195000, satisfaction: 4.6 },
    { month: 'May', orders: 89000, revenue: 540, users: 228000, satisfaction: 4.7 },
    { month: 'Jun', orders: 105000, revenue: 650, users: 265000, satisfaction: 4.8 }
  ];

  const performanceData = [
    { name: 'Q1 2024', orders: 158000, revenue: 980, customers: 435000 },
    { name: 'Q2 2024', orders: 267000, revenue: 1640, customers: 688000 },
    { name: 'Q3 2024', orders: 385000, revenue: 2350, customers: 925000 },
    { name: 'Q4 2024 (Proj)', orders: 520000, revenue: 3200, customers: 1250000 }
  ];

  return (
    <section className="py-16 lg:py-24 bg-gradient-to-br from-gray-50 via-red-50 to-orange-50 relative overflow-hidden" aria-label="Market Analytics and Performance Data">
      {/* Enhanced Background Effects */}
      <div className="absolute inset-0">
        <div className="absolute top-20 left-20 w-80 h-80 bg-red-200 rounded-full mix-blend-multiply filter blur-xl opacity-70 animate-blob"></div>
        <div className="absolute bottom-20 right-20 w-72 h-72 bg-orange-200 rounded-full mix-blend-multiply filter blur-xl opacity-70 animate-blob animation-delay-2000"></div>
        <div className="absolute top-1/2 left-1/2 transform -translate-x-1/2 -translate-y-1/2 w-96 h-96 bg-yellow-200 rounded-full mix-blend-multiply filter blur-xl opacity-70 animate-blob animation-delay-4000"></div>
      </div>
      
      <div className="w-full max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 relative z-10">
        <motion.div 
          className="text-center mb-16 lg:mb-20"
          initial={{ opacity: 0, y: 30 }}
          whileInView={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.8 }}
          viewport={{ once: true }}
        >
          <motion.div 
            className="inline-flex items-center gap-2 bg-gradient-to-r from-red-50 to-orange-50 text-red-500 rounded-2xl px-4 py-2 mb-6"
            initial={{ opacity: 0, scale: 0.8 }}
            whileInView={{ opacity: 1, scale: 1 }}
            transition={{ duration: 0.6, delay: 0.2 }}
            viewport={{ once: true }}
          >
            <BarChart3 size={16} className="sm:w-5 sm:h-5" />
            <span className="font-semibold text-sm">Real-time Analytics</span>
          </motion.div>
          <h2 className="text-3xl lg:text-5xl xl:text-6xl font-bold text-gray-900 mb-6">
            Data-Driven <span className="bg-gradient-to-r from-red-500 to-orange-500 bg-clip-text text-transparent">Insights</span>
          </h2>
          <p className="text-lg lg:text-xl text-gray-600 max-w-4xl mx-auto leading-relaxed">
            Track our explosive growth across India's quick commerce landscape. Real-time metrics showcasing customer satisfaction, market penetration, and operational excellence.
          </p>
        </motion.div>

        {/* Key Metrics Grid */}
        <motion.div 
          className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 xl:grid-cols-6 gap-3 sm:gap-4 mb-12 sm:mb-16"
          initial={{ opacity: 0, y: 40 }}
          whileInView={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.8, delay: 0.3 }}
          viewport={{ once: true }}
        >
          {keyMetrics.map((metric, index) => (
            <motion.div 
              key={index}
              className="bg-white rounded-xl sm:rounded-2xl p-4 sm:p-6 shadow-sm border border-gray-100 hover:shadow-lg transition-all duration-300 group"
              initial={{ opacity: 0, y: 20 }}
              whileInView={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.6, delay: 0.4 + index * 0.1 }}
              viewport={{ once: true }}
              whileHover={{ y: -4 }}
            >
              <div className="flex items-center justify-between mb-3">
                <span className="text-xl sm:text-2xl">{metric.icon}</span>
                <div className={`flex items-center text-xs px-2 py-1 rounded-full ${
                  metric.trend === 'up' 
                    ? 'bg-green-100 text-green-600' 
                    : 'bg-blue-100 text-blue-600'
                }`}>
                  {metric.trend === 'up' ? <ChevronUp size={10} className="sm:w-3 sm:h-3" /> : <ChevronDown size={10} className="sm:w-3 sm:h-3" />}
                  <span className="text-xs">{metric.change}</span>
                </div>
              </div>
              <div className="text-xl sm:text-2xl font-bold text-gray-800 mb-1">{metric.value}</div>
              <div className="text-xs sm:text-sm font-semibold text-gray-700 mb-1">{metric.title}</div>
              <div className="text-xs text-gray-500">{metric.description}</div>
            </motion.div>
          ))}
        </motion.div>

        {/* Enhanced Charts Section */}
        <div className="mb-16">
          {/* Two Column Layout */}
          <div className="grid lg:grid-cols-2 gap-8">
            {/* Category Distribution - Enhanced */}
                         <motion.div 
               className="bg-white/90 backdrop-blur-sm rounded-3xl p-8 shadow-xl border border-gray-200"
               initial={{ opacity: 0, x: -30 }}
               whileInView={{ opacity: 1, x: 0 }}
               transition={{ duration: 0.8, delay: 0.5 }}
               viewport={{ once: true }}
             >
              <div className="flex items-center justify-between mb-8">
                <div>
                  <h3 className="text-xl lg:text-2xl font-bold text-gray-900 mb-2">Market Share</h3>
                  <p className="text-gray-600">Category-wise order distribution</p>
                </div>
                <div className="bg-gradient-to-r from-red-50 to-orange-50 p-3 rounded-2xl">
                  <Package className="w-6 h-6 text-red-500" />
                </div>
              </div>
              
              <div className="h-64 w-full mb-6">
                <ResponsiveContainer width="100%" height="100%">
                  <PieChart>
                    <Pie
                      data={marketData}
                      cx="50%"
                      cy="50%"
                      innerRadius={50}
                      outerRadius={100}
                      paddingAngle={2}
                      dataKey="percentage"
                      nameKey="category"
                    >
                      {marketData.map((entry, index) => (
                        <Cell key={`cell-${index}`} fill={entry.color} />
                      ))}
                    </Pie>
                    <Tooltip 
                      formatter={(value, name) => [`${value}%`, name]}
                      contentStyle={{ 
                        backgroundColor: 'white', 
                        border: 'none',
                        borderRadius: '12px',
                        boxShadow: '0 10px 25px -5px rgba(0, 0, 0, 0.1)',
                        fontSize: '14px'
                      }}
                    />
                  </PieChart>
                </ResponsiveContainer>
              </div>
              
              <div className="space-y-3">
                {marketData.slice(0, 3).map((item, index) => (
                  <div key={index} className="flex items-center justify-between p-3 bg-gray-50 rounded-xl">
                    <div className="flex items-center">
                      <div 
                        className="w-4 h-4 rounded-full mr-3" 
                        style={{ backgroundColor: item.color }}
                      />
                      <span className="font-medium text-gray-800">{item.category}</span>
                    </div>
                    <div className="flex items-center gap-3">
                      <span className="text-sm text-gray-600">{item.percentage}%</span>
                      <span className="text-sm font-semibold text-green-600">{item.growth}</span>
                    </div>
                  </div>
                ))}
              </div>
            </motion.div>

            {/* Growth Trends - Enhanced */}
                         <motion.div 
               className="bg-white/90 backdrop-blur-sm rounded-3xl p-8 shadow-xl border border-gray-200"
               initial={{ opacity: 0, x: 30 }}
               whileInView={{ opacity: 1, x: 0 }}
               transition={{ duration: 0.8, delay: 0.6 }}
               viewport={{ once: true }}
             >
              <div className="flex items-center justify-between mb-8">
                <div>
                  <h3 className="text-xl lg:text-2xl font-bold text-gray-900 mb-2">Growth Momentum</h3>
                  <p className="text-gray-600">Monthly progression trends</p>
                </div>
                <div className="bg-gradient-to-r from-red-50 to-orange-50 p-3 rounded-2xl">
                  <TrendingUp className="w-6 h-6 text-red-500" />
                </div>
              </div>
              
              <div className="h-64 w-full mb-6">
                <ResponsiveContainer width="100%" height="100%">
                  <LineChart data={growthData} margin={{ top: 20, right: 30, left: 20, bottom: 5 }}>
                    <CartesianGrid strokeDasharray="3 3" stroke="#f1f5f9" strokeOpacity={0.8} />
                    <XAxis 
                      dataKey="month" 
                      axisLine={false}
                      tickLine={false}
                      style={{ fontSize: '12px', fill: '#64748b' }}
                    />
                    <YAxis 
                      axisLine={false}
                      tickLine={false}
                      style={{ fontSize: '12px', fill: '#64748b' }}
                    />
                    <Tooltip 
                      contentStyle={{ 
                        backgroundColor: 'white', 
                        border: 'none',
                        borderRadius: '12px',
                        boxShadow: '0 10px 25px -5px rgba(0, 0, 0, 0.1)',
                        fontSize: '14px'
                      }}
                    />
                    <Line 
                      type="monotone" 
                      dataKey="orders" 
                      stroke="#ef4444"
                      strokeWidth={3}
                      dot={{ fill: '#ef4444', strokeWidth: 2, r: 6 }}
                      activeDot={{ r: 8, fill: '#ef4444' }}
                      name="Orders"
                    />
                    <Line 
                      type="monotone" 
                      dataKey="users" 
                      stroke="#f59e0b"
                      strokeWidth={3}
                      dot={{ fill: '#f59e0b', strokeWidth: 2, r: 6 }}
                      activeDot={{ r: 8, fill: '#f59e0b' }}
                      name="Users"
                    />
                  </LineChart>
                </ResponsiveContainer>
              </div>
              
              <div className="grid grid-cols-2 gap-4">
                <div className="text-center p-3 bg-red-50 rounded-xl">
                  <div className="text-lg font-bold text-red-600">134%</div>
                  <div className="text-sm text-gray-600">Growth Rate</div>
                </div>
                <div className="text-center p-3 bg-orange-50 rounded-xl">
                  <div className="text-lg font-bold text-orange-600">4.8★</div>
                  <div className="text-sm text-gray-600">Satisfaction</div>
                </div>
              </div>
            </motion.div>
          </div>
        </div>
      </div>
    </section>
  );
};

export default DataAnalytics; 